/**
 * Team & Workload — the people and work behind each department's numbers.
 *
 * What matters on these screens is which fields actually move the model and which do not,
 * so each one says so plainly: Junior/Mid/Senior/Lead affects only Team Fragility and
 * Absence Exposure, never a department's live status; Anticipated Demand is forward-looking
 * and never folded into today's gap; Team Output is a pilot diagnostic that never reaches a
 * hiring verdict.
 */
import { useMemo, useState } from 'react';
import { useParams, NavLink, Navigate } from 'react-router-dom';
import { useModel } from '../state.jsx';
import { Icon } from '../components/IconSprite.jsx';

const NAV = [
  ['People', [
    ['team', 'Team Roster', 'ic-users'],
    ['output', 'Team Output', 'ic-gauge', 'Pilot'],
    ['outages', 'Known Outages', 'ic-cal-x'],
    ['succession', 'Cascading Roles', 'ic-branch'],
  ]],
  ['Work & Demand', [
    ['pipeline', 'Anticipated Demand', 'ic-trend'],
    ['projects', 'Active Projects', 'ic-wrench'],
    ['clients', 'Demand Sources', 'ic-building'],
  ]],
];

const SENIORITY = ['junior', 'mid', 'senior', 'lead'];

/** Inline field bound to one record, saved on blur. */
function Field({ collection, record, field, type = 'text', options, style, width }) {
  const { patch, isAdmin } = useModel();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const value = record[field];

  async function commit(next) {
    if (next === value) { setDraft(null); return; }
    setBusy(true);
    try { await patch(collection, record.id, { [field]: next }); }
    finally { setBusy(false); setDraft(null); }
  }

  if (!isAdmin) return <span>{value ?? '—'}</span>;

  if (options) {
    return (
      <select className="ei" disabled={busy} value={value ?? ''} style={{ width, ...style }}
        onChange={(e) => commit(e.target.value)}>
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      className={'ei' + (type === 'number' ? ' num' : '')}
      type={type} disabled={busy} style={{ width, ...style }}
      value={draft ?? value ?? ''}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(type === 'number' ? Number(e.target.value) : e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
      }}
    />
  );
}

function PageHead({ icon, title, sub, actions }) {
  return (
    <div className="page-hdr">
      <div className="ph-row">
        <div className="ph-icon"><Icon name={icon} /></div>
        <div>
          <div className="page-hdr-title">{title}</div>
          <div className="page-hdr-sub">{sub}</div>
        </div>
        {actions && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

function useLookups() {
  const { state } = useModel();
  return useMemo(() => ({
    deptName: (id) => state.depts.find((d) => d.id === id)?.name ?? id,
    roleName: (id) => state.roles.find((r) => r.id === id)?.name ?? id,
    empName: (id) => state.team.find((e) => e.id === id)?.name ?? id,
    deptOptions: state.depts.map((d) => ({ value: d.id, label: d.name })),
    roleOptions: state.roles.map((r) => ({ value: r.id, label: r.name })),
  }), [state]);
}

/** Groups rows under a teal department header, as the legacy roster does. */
function grouped(records, deptOf, deptName) {
  const map = new Map();
  for (const r of records) {
    const key = deptOf(r);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return [...map.entries()]
    .map(([key, rows]) => ({ key, name: deptName(key), rows }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function TeamRoster() {
  const { state, engine, patch, isAdmin } = useModel();
  const { deptName, deptOptions, roleOptions } = useLookups();

  const groups = grouped(state.team, (e) => e.dept, deptName);
  const filled = state.team.filter((e) => !e.vacant && !e._builtStale).length;
  const vacant = state.team.filter((e) => e.vacant).length;

  return (
    <>
      <PageHead
        icon="ic-users"
        title="Team Roster"
        sub="Active employees, seniority, departure probability, and capacity settings"
      />
      <div className="card">
        <div className="card-body flush">
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th><th>Department</th><th>Role</th><th>Seniority</th>
                  <th style={{ textAlign: 'right' }}>Hrs/wk</th>
                  <th colSpan={2} style={{ textAlign: 'right' }}>Departure risk</th>
                  <th>Vacant?</th><th />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <>
                    <tr key={g.key} className="grp">
                      <td colSpan={9}>▶ {g.name}</td>
                    </tr>
                    {g.rows.map((e) => {
                      const adjusted = engine.empBillCapAdjusted(e);
                      const raw = adjusted / (engine.seniorityMult(e) || 1);
                      return (
                        <tr key={e.id}>
                          <td><Field collection="team" record={e} field="name" /></td>
                          <td><Field collection="team" record={e} field="dept" options={deptOptions} /></td>
                          <td><Field collection="team" record={e} field="roleId" options={roleOptions} /></td>
                          <td><Field collection="team" record={e} field="seniority" options={SENIORITY} /></td>
                          <td style={{ textAlign: 'right' }}>
                            <Field collection="team" record={e} field="hrs" type="number" width={54} />
                          </td>
                          <td style={{ textAlign: 'right' }} className="num-mono">
                            <span style={{ color: e.vacant ? 'var(--amber)' : 'var(--teal)' }}>
                              {e.vacant ? '—' : `${adjusted.toFixed(1)} hrs`}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }} className="num-mono" title="Before the seniority multiplier">
                            {e.vacant ? '—' : `${raw.toFixed(1)} hrs`}
                          </td>
                          <td>
                            <input
                              type="checkbox" checked={!!e.vacant} disabled={!isAdmin}
                              onChange={(ev) => patch('team', e.id, { vacant: ev.target.checked })}
                            />{' '}
                            <span
                              className={`chip ${e._builtStale ? 'chip-grey' : e.vacant ? 'chip-amber' : 'chip-green'}`}
                              style={{ fontSize: 9 }}
                            >
                              {e._builtStale ? 'Archived' : e.vacant
                                ? (engine.isVacancyRecruitable(e) ? 'Vacant' : 'Not funded')
                                : 'Filled'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', color: 'var(--red)' }}>✕</td>
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <p className="page-note">
        {filled} filled · {vacant} vacant · {state.team.length} records. Seniority and departure
        risk feed Team Fragility and Coverage Risk's Absence Exposure table only — they have no
        effect on any department's volume, coverage or support status. Vacancies are never added
        by hand: they are created from Built Positions and reconciled against that register on
        every load, always linked to a specific position row. A record marked Archived is one
        Built's latest export no longer lists; it is kept rather than deleted so outage and Team
        Output records pointing at that person are never orphaned.
      </p>
    </>
  );
}

function SimpleTable({ icon, title, sub, note, collection, records, columns, empty }) {
  const { state } = useModel();
  const { deptName } = useLookups();
  const groups = columns.some((c) => c.field === 'dept')
    ? grouped(records, (r) => r.dept, deptName)
    : null;

  const body = (rows) => rows.map((r) => (
    <tr key={r.id}>
      {columns.map((c) => (
        <td key={c.field ?? c.label} style={{ textAlign: c.align }}>
          {c.render ? c.render(r) : (
            <Field collection={collection} record={r} field={c.field} type={c.type} options={c.options} />
          )}
        </td>
      ))}
    </tr>
  ));

  return (
    <>
      <PageHead icon={icon} title={title} sub={sub} />
      <div className="card">
        <div className="card-body flush">
          {records.length === 0 ? (
            <div style={{ padding: 22, fontSize: 12, color: 'var(--ink3)' }}>{empty}</div>
          ) : (
            <div className="table-scroll">
              <table className="tbl">
                <thead>
                  <tr>{columns.map((c) => <th key={c.field ?? c.label} style={{ textAlign: c.align }}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {groups
                    ? groups.map((g) => (
                      <>
                        <tr key={g.key} className="grp"><td colSpan={columns.length}>▶ {g.name}</td></tr>
                        {body(g.rows)}
                      </>
                    ))
                    : body(records)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {note && <p className="page-note">{note}</p>}
    </>
  );
}

function Outages() {
  const { state } = useModel();
  const { empName } = useLookups();
  return (
    <SimpleTable
      icon="ic-cal-x" title="Known Outages"
      sub="Entered leaves of absence — someone out now is not available capacity today"
      collection="outages" records={state.outages}
      empty="No outages recorded."
      columns={[
        { label: 'Employee', field: 'empId', render: (o) => empName(o.empId) },
        { label: 'Reason', field: 'reason' },
        { label: 'Starts (wk)', field: 'startWk', type: 'number', align: 'right' },
        { label: 'Weeks', field: 'durationWks', type: 'number', align: 'right' },
        { label: 'Cover', field: 'coverageType', options: ['none', 'contractor', 'internal'] },
      ]}
      note="An active outage reduces that person's contribution to productive FTE, and a contractor-covered outage counts at the same 80% availability rate used everywhere else. An outage in a coverage department does not change a grid cell — it marks that department's coverage as needing revalidation instead."
    />
  );
}

function Pipeline() {
  const { state } = useModel();
  const { deptOptions } = useLookups();
  return (
    <SimpleTable
      icon="ic-trend" title="Anticipated Demand"
      sub={`Prospects expected to close within the ${state.settings.hz}-week forward planning horizon`}
      collection="pipeline" records={state.pipeline}
      empty="No anticipated demand recorded."
      columns={[
        { label: 'Prospect', field: 'name' },
        { label: 'Department', field: 'dept', options: deptOptions },
        { label: 'Service hrs/wk', field: 'svcHrs', type: 'number', align: 'right' },
        { label: 'Probability', field: 'prob', type: 'number', align: 'right' },
        { label: 'Closes (wk)', field: 'closeWk', type: 'number', align: 'right' },
        {
          label: 'Weighted hrs', align: 'right',
          render: (p) => <span className="num-mono">{Math.round((p.svcHrs ?? 0) * (p.prob ?? 1))}</span>,
        },
      ]}
      note="Deliberately NOT added to implied FTE, the gap, or any department's status. This is probabilistic future business, not confirmed work — letting an uncertain deal flip a status could trigger a real hiring decision over something that may not happen. It is shown as a separate, clearly-labelled forward-looking figure instead."
    />
  );
}

function Projects() {
  const { state } = useModel();
  const { deptOptions } = useLookups();
  return (
    <SimpleTable
      icon="ic-wrench" title="Active Projects"
      sub="Time-bounded work with a defined end"
      collection="impls" records={state.impls}
      empty="No active projects."
      columns={[
        { label: 'Project', field: 'name' },
        { label: 'Department', field: 'dept', options: deptOptions },
        { label: 'Hrs/wk', field: 'hrs', type: 'number', align: 'right' },
        { label: 'Weeks left', field: 'wksLeft', type: 'number', align: 'right' },
        {
          label: 'FTE equivalent', align: 'right',
          render: (p) => <span className="num-mono">{p.wksLeft > 0 ? ((p.hrs ?? 0) / 40).toFixed(2) : '0.00'}</span>,
        },
      ]}
      note="Project hours DO add to a volume-threshold department's implied demand — a project is real, if temporary, work. They are summed and rounded to two decimals rather than ceiled to a whole FTE: ceiling them would mean a one-hour-a-week project added a full phantom FTE, identical to a forty-hour one. A project whose weeks-left has reached zero contributes nothing."
    />
  );
}

function Clients() {
  const { state } = useModel();
  const { deptOptions } = useLookups();
  return (
    <SimpleTable
      icon="ic-building" title="Demand Sources"
      sub="Clients and internal populations each department serves"
      collection="clients" records={state.clients}
      empty="No demand sources."
      columns={[
        { label: 'Demand source', field: 'name' },
        { label: 'Department', field: 'dept', options: deptOptions },
        { label: 'Tier', field: 'tier', options: ['T1', 'T2', 'T3'] },
        { label: 'Hrs/wk', field: 'hrs', type: 'number', align: 'right' },
        { label: 'Churn risk', field: 'churnProb', type: 'number', align: 'right' },
        {
          label: 'Revenue-bearing', align: 'center',
          render: (c) => (
            <span className={`chip ${c.revenueBearing ? 'chip-green' : 'chip-grey'}`}>
              {c.revenueBearing ? 'Yes' : 'Internal'}
            </span>
          ),
        },
      ]}
      note="Marking a source as not revenue-bearing suppresses every dollar figure for that scope — Business Case shows N/A rather than a fabricated number. The verdict still computes off the real gap; it simply has no dollar case behind it."
    />
  );
}

function Succession() {
  const { state } = useModel();
  const { deptOptions } = useLookups();
  return (
    <SimpleTable
      icon="ic-branch" title="Cascading Roles"
      sub="What a single departure or promotion sets off downstream"
      collection="successions" records={state.successions}
      empty="No cascading-role scenarios defined."
      columns={[
        { label: 'Scenario', field: 'name' },
        { label: 'Department', field: 'dept', options: deptOptions },
        { label: 'Trigger', field: 'trigger', options: ['departure', 'promotion'] },
        { label: 'Chain', render: (s) => `${s.chain?.length ?? 0} step(s)` },
      ]}
      note="Planning aid only — cascading roles never feed a department's live status or a hiring verdict."
    />
  );
}

function TeamOutput() {
  const { state, engine } = useModel();
  const { deptName } = useLookups();

  const rows = useMemo(() => {
    const volDepts = state.depts.filter((d) => d.volDrivers?.length);
    return volDepts.flatMap((dep) =>
      state.team
        .filter((e) => e.dept === dep.id && !e.vacant && !e._builtStale)
        .map((e) => {
          const driver = dep.volDrivers[0];
          return {
            id: e.id, name: e.name, dept: deptName(dep.id), driver: driver.name,
            expected: Math.round(engine.empRampMult(e) * ((e.hrs ?? 40) / 40) * (driver.perFTE ?? 0)),
          };
        }),
    );
  }, [state, engine, deptName]);

  return (
    <>
      <PageHead
        icon="ic-gauge" title="Team Output"
        sub="Actual output vs. expected pace, for volume-threshold departments only"
        actions={<span className="chip chip-red">PILOT</span>}
      />
      <div className="alert" style={{ background: 'var(--ltred)', color: 'var(--ink2)' }}>
        <span>⚠️</span>
        <span>
          Informational only, and <strong>not validated for individual performance assessment</strong>.
          Expected output here is ramp- and hours-weighted but does not apply the org-wide
          PTO/holiday/sick assumption or an active outage, so it is deliberately not the same
          number as productive FTE — treat it as ramp-only. It never feeds productive FTE, a
          department's status, a gap, or a hiring verdict.
        </span>
      </div>
      <div className="card">
        <div className="card-body flush">
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th><th>Department</th><th>Driver</th>
                  <th style={{ textAlign: 'right' }}>Expected / month</th>
                  <th style={{ textAlign: 'right' }}>Actual</th>
                  <th style={{ textAlign: 'right' }}>Ratio</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>{r.dept}</td>
                    <td>{r.driver}</td>
                    <td style={{ textAlign: 'right' }} className="num-mono">{r.expected.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }} className="num-mono">—</td>
                    <td style={{ textAlign: 'right' }} className="num-mono">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <p className="page-note">
        A team rollup is suppressed entirely when employees have logged different reporting
        periods — "Mixed reporting periods — team rollup unavailable" — rather than blending two
        different months into one number.
      </p>
    </>
  );
}

const SECTIONS = {
  team: TeamRoster, outages: Outages, pipeline: Pipeline,
  projects: Projects, clients: Clients, succession: Succession, output: TeamOutput,
};

export function Manage() {
  const { state } = useModel();
  const { section } = useParams();
  const View = SECTIONS[section];
  if (!View) return <Navigate to="/manage/team" replace />;

  const counts = {
    team: state.team.length,
    outages: state.outages.length,
    pipeline: state.pipeline.length,
    projects: state.impls.length,
    clients: state.clients.length,
    succession: state.successions.length,
  };

  return (
    <div className="page">
      <div className="manage-layout">
        <nav className="manage-nav">
          {NAV.map(([group, items]) => (
            <div key={group}>
              <div className="manage-nav-group">{group}</div>
              {items.map(([key, label, icon, badge]) => (
                <NavLink
                  key={key}
                  to={`/manage/${key}`}
                  className={({ isActive }) => 'manage-nav-item' + (isActive ? ' on' : '')}
                >
                  <Icon name={icon} style={{ width: 14, height: 14 }} />
                  {label}
                  {badge
                    ? <span className="chip chip-red" style={{ marginLeft: 'auto', fontSize: 9 }}>{badge}</span>
                    : counts[key] != null && <span className="badge">{counts[key]}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="manage-section on">
          <View />
        </div>
      </div>
    </div>
  );
}
